export const MDM_TO_RECORD_TYPE = {
  account: 'companies',
  contact: 'people',
  lead: 'people',
  user: 'people',
} as const;

// display names for user source person_name, person_email, person_phone mdm property fields
export const ACCOUNT_OWNER_DISPLAY = 'Account Owner';
export const ACCOUNT_OWNER_NAME_DISPLAY = 'Account Owner Name';
export const ACCOUNT_OWNER_EMAIL_DISPLAY = 'Account Owner Email';
export const ACCOUNT_OWNER_PHONE_DISPLAY = 'Account Owner Phone';
export const AO_DISPLAY = 'AO';

// source mdm types
export const MDM_TYPES = {
  ACCOUNT: 'account',
  CONTACT: 'contact',
  DEAL: 'deal',
  DEAL_CONTACT: 'deal_contact_role',
  PRODUCT: 'product',
  OPPORTUNITY_LINE_ITEM: 'deal_product',
  LEAD: 'lead',
  USER: 'user',
  PARTNER_CANDIDATE: 'partner_candidate',
  ACCOUNT_PRODUCT: 'account_product',
} as const;
export type MdmType = (typeof MDM_TYPES)[keyof typeof MDM_TYPES];

// source field mdm properties
export const MDM_PROPERTIES = {
  [MDM_TYPES.ACCOUNT]: {
    BILLING_COUNTRY: 'company_country',
    CREATED_AT: 'company_created_at',
    EMPLOYEES: 'company_employees',
    ID: 'company_key',
    INDUSTRY: 'company_industry',
    NAME: 'company_name',
    REGION: 'company_region',
    TYPE: 'company_type',
    WEBSITE: 'company_website',
  },
  [MDM_TYPES.CONTACT]: {
    CREATED_AT: 'person_created_at',
    EMAIL: 'person_email',
    FIRST_NAME: 'person_first_name',
    LAST_ACTIVITY_AT: 'person_last_activity_at',
    LAST_NAME: 'person_last_name',
    PHONE: 'person_phone',
    NAME: 'person_name',
    TITLE: 'person_title',
  },
  [MDM_TYPES.DEAL]: {
    AMOUNT: 'deal_amount',
    CLOSE_DATE: 'deal_close_date',
    IS_CLOSED: 'deal_is_closed',
    IS_WON: 'deal_is_won',
    LAST_ACTIVITY_AT: 'deal_last_activity_at',
    NAME: 'deal_name',
    OPEN_DATE: 'deal_open_date',
    STAGE: 'deal_stage',
    OWNER_ID: 'deal_owner_id',
  },
  [MDM_TYPES.DEAL_CONTACT]: {
    IS_PRIMARY: 'deal_contact_role_is_primary',
    ROLE: 'deal_contact_role_name',
  },
  [MDM_TYPES.LEAD]: {
    CREATED_AT: 'person_created_at',
    EMAIL: 'person_email',
    ID: 'person_key',
    NAME: 'person_name',
    PHONE: 'person_phone',
    TITLE: 'person_title',
  },
  [MDM_TYPES.USER]: {
    EMAIL: 'person_email',
    FIRST_NAME: 'person_first_name',
    IS_ACTIVE: 'person_is_active',
    LAST_NAME: 'person_last_name',
    NAME: 'person_name',
    PHONE: 'person_phone',
    TITLE: 'person_title',
  },
  [MDM_TYPES.PRODUCT]: {
    ID: 'product_key',
    NAME: 'product_name',
  },
  [MDM_TYPES.OPPORTUNITY_LINE_ITEM]: {
    ID: 'deal_product_key',
  },
  [MDM_TYPES.ACCOUNT_PRODUCT]: {
    ID: 'account_product_key',
    COMPANY_ID: 'account_product_company_id',
    NAME: 'account_product_name',
    STATUS: 'account_product_status',
  },
} as const;

type MdmProperties = typeof MDM_PROPERTIES;
type MdmPropertyGroup = keyof MdmProperties;
export type MdmPropertyValue<T extends MdmPropertyGroup> =
  MdmProperties[T][keyof MdmProperties[T]];

export type MdmProperty = {
  [K in MdmPropertyGroup]: MdmPropertyValue<K>;
}[MdmPropertyGroup];

export const ACCOUNT_PRODUCT_DISPLAY_NAME = 'Account Product';

// MDM types excluded from report column source selection.
// account_product (e.g. Dell account programs) is filterable but excluded as a
// report column for now; supporting it as a column is deferred until we work on
// the first GA release.
export const EXCLUDED_COLUMN_MDM_TYPES: string[] = [
  MDM_TYPES.CONTACT,
  MDM_TYPES.PRODUCT,
  MDM_TYPES.OPPORTUNITY_LINE_ITEM,
  MDM_TYPES.ACCOUNT_PRODUCT,
];

// MS Dynamics does not have a field with mdm property deal is closed, need to go off column name
export const MS_DYNAMICS_IS_CLOSED_COLUMN = 'statecode' as const;
